import {FC} from 'react';
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    makeStyles,
    mergeClasses,
    shorthands,
    tokens
} from '@fluentui/react-components';

import parse, { DOMNode, Element, HTMLReactParserOptions, domToReact } from 'html-react-parser';
import {NewsResponse} from '../../../libs/services/NewsService';
import {NewsRead} from "../../../libs/services/SettingsService";

const useClasses = makeStyles({
    newsItem: {
        ...shorthands.margin(tokens.spacingVerticalM, '0'),
        ...shorthands.padding(tokens.spacingVerticalS),
        ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground3,
        },
    },
    unreadNewsTitle: {
        fontWeight: '600',
        color: tokens.colorBrandForeground1,
    },
    newsTitle: {
        fontWeight: '400',
        fontSize: tokens.fontSizeBase300,
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        hyphens: 'auto',
    },
    newsDate: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        marginTop: tokens.spacingVerticalS,
    },
    newsContent: {
        fontSize: tokens.fontSizeBase300,
        marginTop: tokens.spacingVerticalS,
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        hyphens: 'auto',
        '& img': {
            maxWidth: '100%',
            maxHeight: '400px',
            height: 'auto',
        },
    },
    headerContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    unreadNews: {
        position: 'relative',
        paddingLeft: '20px',
        '::before': {
            content: '""',
            display: 'block',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: tokens.colorBrandForeground1,
            position: 'absolute',
            left: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
        }
    },
    expandedNewsItem: {
        backgroundColor: tokens.colorNeutralBackground3,
        borderTopRightRadius: '4px',
        borderBottomRightRadius: '4px',
        ...shorthands.borderLeft('4px', 'solid', '#08204a'),
    },
});

const options: HTMLReactParserOptions = {
    replace: (domNode: DOMNode) => {
        if (
            domNode instanceof Element &&
            domNode.name === 'a' &&
            domNode.attribs.href
        ) {
            return (
                <a
                    {...domNode.attribs}
                    target="_blank"
                    rel="noreferrer"
                >
                    {domToReact(domNode.children as DOMNode[], options)}
                </a>
            );
        }
        return;
    },
};

interface NewsfeedAccordionProps {
    newsItems: NewsResponse[];
    readNewsIds: NewsRead[];
    openAccordionItems: string[];
    setOpenAccordionItems: (items: string[]) => void;
    markNewsAsRead: (id: string) => void;
    formatDate: (date: string | Date) => string;
}

export const NewsfeedAccordion: FC<NewsfeedAccordionProps> = ({
    newsItems,
    readNewsIds,
    openAccordionItems,
    setOpenAccordionItems,
    markNewsAsRead,
    formatDate
}) => {
    const classes = useClasses();

    return (
        <Accordion collapsible={true}
           openItems={openAccordionItems}
           onToggle={(_, data) => {
               setOpenAccordionItems(data.openItems as string[]);
               if (data.openItems.includes(data.value)) {
                   const newsId = String(data.value);
                   markNewsAsRead(newsId);
               }
           }}
        >
            {newsItems.map((news) => (
                <AccordionItem
                    key={news.id}
                    value={news.id}
                    className={mergeClasses(
                        classes.newsItem,
                        !readNewsIds.some(item => item.id === news.id) && classes.unreadNews,
                        openAccordionItems.includes(news.id) && classes.expandedNewsItem
                    )}
                >
                    <AccordionHeader expandIcon={<></>}>
                        <div className={classes.headerContainer}>
                            <div
                                className={mergeClasses(
                                    classes.newsTitle,
                                    !readNewsIds.some(item => item.id === news.id) && classes.unreadNewsTitle
                                )}
                            >{news.title}</div>
                            <div className={classes.newsDate}>{formatDate(news.createdAt)}</div>
                        </div>
                    </AccordionHeader>
                    <AccordionPanel>
                        <div className={classes.newsContent}>
                            {parse(news.content, options)}
                        </div>
                    </AccordionPanel>
                </AccordionItem>
            ))}
        </Accordion>
    );
};
